import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/Form/FormInput';
import FormTextarea from '@/Components/Form/FormTextarea';
import FormSelect from '@/Components/Form/FormSelect';
import FormSection from '@/Components/Form/FormSection';

export default function QueriesCreate({ clients, destinations, users, statuses, types, priorities, sources }) {
    const { data, setData, post, processing, errors } = useForm({
        title: '',
        client_id: '',
        guest_name: '',
        contact_email: '',
        contact_phone: '',
        travel_date: '',
        return_date: '',
        nights: 0,
        adults: 2,
        children: 0,
        infants: 0,
        destinations: [],
        query_type: 'package',
        status: 'pending',
        priority: 'warm',
        source: '',
        assigned_to: '',
        budget: '',
        quoted_amount: '',
        requirements: '',
        internal_notes: '',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post('/queries');
    };

    const handleClientChange = (clientId) => {
        setData('client_id', clientId);
        if (clientId) {
            const client = clients.find(c => c.id === parseInt(clientId));
            if (client) {
                setData(prev => ({
                    ...prev,
                    client_id: clientId,
                    guest_name: client.name || prev.guest_name,
                    contact_email: client.email || prev.contact_email,
                    contact_phone: client.phone || prev.contact_phone,
                }));
            }
        }
    };

    const calculateNights = (startDate, endDate) => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
            return diff > 0 ? diff : 0;
        }
        return 0;
    };

    const handleDateChange = (field, value) => {
        const newData = { ...data, [field]: value };
        if (field === 'travel_date' || field === 'return_date') {
            newData.nights = calculateNights(
                field === 'travel_date' ? value : data.travel_date,
                field === 'return_date' ? value : data.return_date
            );
        }
        setData(newData);
    };

    const typeOptions = Object.entries(types).map(([key, label]) => ({ id: key, name: label }));
    const priorityOptions = Object.entries(priorities).map(([key, label]) => ({ id: key, name: label }));
    const sourceOptions = Object.entries(sources).map(([key, label]) => ({ id: key, name: label }));
    const statusOptions = Object.entries(statuses).map(([key, label]) => ({ id: key, name: label }));

    return (
        <AppLayout title="New Query">
            <PageHeader
                title="New Query"
                subtitle="Create a new travel inquiry"
                breadcrumbs={[
                    { label: 'CRM' },
                    { label: 'Queries', href: '/queries' },
                    { label: 'Create' },
                ]}
            />

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-lg-8">
                        <FormSection title="Query Details">
                            <FormInput
                                className="col-12"
                                label="Query Title"
                                name="title"
                                value={data.title}
                                onChange={(v) => setData('title', v)}
                                error={errors.title}
                                required
                                placeholder="e.g., 5 Nights Bali Honeymoon Package"
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Query Type"
                                name="query_type"
                                value={data.query_type}
                                onChange={(v) => setData('query_type', v)}
                                options={typeOptions}
                                required
                                searchable
                                placeholder="Select Type"
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Priority"
                                name="priority"
                                value={data.priority}
                                onChange={(v) => setData('priority', v)}
                                options={priorityOptions}
                                required
                                searchable
                                placeholder="Select Priority"
                            />
                            <FormSelect
                                className="col-md-4"
                                label="Lead Source"
                                name="source"
                                value={data.source}
                                onChange={(v) => setData('source', v)}
                                options={sourceOptions}
                                searchable
                                placeholder="Select Source"
                            />
                        </FormSection>

                        <FormSection title="Client / Guest Information">
                            <FormSelect
                                className="col-md-6"
                                label="Select Existing Client"
                                name="client_id"
                                value={data.client_id}
                                onChange={handleClientChange}
                                options={clients}
                                searchable
                                placeholder="Search clients..."
                            />
                            <FormInput
                                className="col-md-6"
                                label="Guest Name"
                                name="guest_name"
                                value={data.guest_name}
                                onChange={(v) => setData('guest_name', v)}
                                placeholder="Primary traveler name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Email"
                                name="contact_email"
                                type="email"
                                value={data.contact_email}
                                onChange={(v) => setData('contact_email', v)}
                                error={errors.contact_email}
                                placeholder="guest@email.com"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Phone"
                                name="contact_phone"
                                value={data.contact_phone}
                                onChange={(v) => setData('contact_phone', v)}
                                placeholder="+91 9876543210"
                            />
                        </FormSection>

                        <FormSection title="Travel Details">
                            <FormInput
                                className="col-md-4"
                                label="Travel Date"
                                name="travel_date"
                                type="date"
                                value={data.travel_date}
                                onChange={(v) => handleDateChange('travel_date', v)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Return Date"
                                name="return_date"
                                type="date"
                                value={data.return_date}
                                onChange={(v) => handleDateChange('return_date', v)}
                            />
                            <FormInput
                                className="col-md-4"
                                label="Nights"
                                name="nights"
                                type="number"
                                value={data.nights}
                                onChange={(v) => setData('nights', parseInt(v) || 0)}
                                min="0"
                            />
                            <FormInput
                                className="col-md-4"
                                label="Adults"
                                name="adults"
                                type="number"
                                value={data.adults}
                                onChange={(v) => setData('adults', parseInt(v) || 1)}
                                error={errors.adults}
                                required
                                min="1"
                            />
                            <FormInput
                                className="col-md-4"
                                label="Children"
                                name="children"
                                type="number"
                                value={data.children}
                                onChange={(v) => setData('children', parseInt(v) || 0)}
                                min="0"
                            />
                            <FormInput
                                className="col-md-4"
                                label="Infants"
                                name="infants"
                                type="number"
                                value={data.infants}
                                onChange={(v) => setData('infants', parseInt(v) || 0)}
                                min="0"
                            />
                            <FormTextarea
                                className="col-12"
                                label="Requirements"
                                name="requirements"
                                value={data.requirements}
                                onChange={(v) => setData('requirements', v)}
                                rows={3}
                                placeholder="Special requirements, preferences, or notes from the client..."
                            />
                        </FormSection>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Create Query
                                    </>
                                )}
                            </button>
                            <Link href="/queries" className="btn btn-outline-secondary">Cancel</Link>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <FormSection title="Status & Assignment">
                            <FormSelect
                                className="col-12"
                                label="Status"
                                name="status"
                                value={data.status}
                                onChange={(v) => setData('status', v)}
                                options={statusOptions}
                                searchable
                                placeholder="Select Status"
                            />
                            <FormSelect
                                className="col-12"
                                label="Assigned To"
                                name="assigned_to"
                                value={data.assigned_to}
                                onChange={(v) => setData('assigned_to', v)}
                                options={users}
                                searchable
                                placeholder="Select User"
                            />
                        </FormSection>

                        <FormSection title="Budget & Pricing">
                            <FormInput
                                className="col-12"
                                label="Client Budget (INR)"
                                name="budget"
                                type="number"
                                value={data.budget}
                                onChange={(v) => setData('budget', v)}
                                placeholder="0"
                                min="0"
                            />
                            <FormInput
                                className="col-12"
                                label="Quoted Amount (INR)"
                                name="quoted_amount"
                                type="number"
                                value={data.quoted_amount}
                                onChange={(v) => setData('quoted_amount', v)}
                                placeholder="0"
                                min="0"
                            />
                        </FormSection>

                        <FormSection title="Internal Notes">
                            <FormTextarea
                                className="col-12"
                                name="internal_notes"
                                value={data.internal_notes}
                                onChange={(v) => setData('internal_notes', v)}
                                rows={4}
                                placeholder="Notes visible only to staff..."
                            />
                        </FormSection>
                    </div>
                </div>
            </form>
        </AppLayout>
    );
}
